/* eslint-disable react/no-array-index-key */
import {
  UseDisclosureReturn,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Box,
  Button,
  chakra,
  BoxProps,
  Icon,
  Spinner,
  ButtonProps,
} from "@chakra-ui/react";
import { EmrRemove } from "@medstonetech/slate-icons";
import * as React from "react";

type VitalsInputModalProps = React.PropsWithChildren<
  Pick<UseDisclosureReturn, "isOpen" | "onClose">
> & { title: string };

type BaseVitalsInputModalProps = {
  onSave: (value: string) => void;
  onValidate?: (value: string) => string | boolean;
  initialValue?: string;
} & VitalsInputModalProps;

type VitalsInputModalValueBoxProps = {
  selector?: React.ReactNode;
  error?: string;
} & BoxProps;

const NON_ZERO_KEYPAD_INTS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

function VitalsInputModalValueBox(props: VitalsInputModalValueBoxProps) {
  const { children, selector, error, ...restProps } = props;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="center"
      padding="0 15px"
      position="relative"
      {...restProps}
    >
      {selector}
      <Box display="flex" alignItems="center" height="92px" maxWidth="100%">
        <chakra.span
          fontWeight="400"
          fontSize="2.5rem"
          overflow="hidden"
          maxWidth="100%"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {children}
        </chakra.span>
      </Box>
      {error && (
        <Box position="absolute" bottom="0" fontSize="0.75rem" color="red">
          {error}
        </Box>
      )}
    </Box>
  );
}

type VitalsInputModalKeypadExtraKey = {
  onClick: () => void;
  label: string;
};

type VitalsInputModalKeypadExtraKeys = [
  VitalsInputModalKeypadExtraKey,
  VitalsInputModalKeypadExtraKey,
  VitalsInputModalKeypadExtraKey
];

type VitalsInputModalKeypadProps = {
  onClickKey: (value: string) => void;
  onSave: () => void;
  onDelete: () => void;
  customKeyLabel?: string;
  onClickCustomKey?: () => void;
  extraKeys?: VitalsInputModalKeypadExtraKeys;
  isLoading?: boolean;
  customKeyboardKeys?: readonly string[];
  onKeyboardCustomKey?: (key: string) => void;
};

const DIGIT_KEY_PATTERN = /^[0-9]$/;
const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';
const INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "summary",
  '[role="button"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
].join(", ");

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

function isInteractiveTarget(target: EventTarget | null) {
  return (
    target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null
  );
}

type InputModality = "pointer" | "keyboard";

const VitalsInputModalOpenContext = React.createContext(true);

function useKeypadKeyboardInput(props: VitalsInputModalKeypadProps) {
  const keypadRef = React.useRef<HTMLDivElement>(null);
  // Stays mounted through the close animation, so closed modals must ignore keys.
  const isModalOpen = React.useContext(VitalsInputModalOpenContext);
  const propsRef = React.useRef(props);
  const isModalOpenRef = React.useRef(isModalOpen);

  React.useLayoutEffect(() => {
    propsRef.current = props;
    isModalOpenRef.current = isModalOpen;
  });

  React.useEffect(() => {
    const root: EventTarget =
      keypadRef.current?.closest(DIALOG_SELECTOR) ?? window;
    let modality: InputModality = "pointer";

    const onPointerDown = () => {
      modality = "pointer";
    };

    const onKeyDown = (e: Event) => {
      if (!(e instanceof KeyboardEvent)) {
        return;
      }
      if (e.key === "Tab") {
        modality = "keyboard";
        return;
      }
      if (!isModalOpenRef.current) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) {
        return;
      }
      if (isEditableTarget(e.target)) {
        return;
      }

      const {
        onClickKey,
        onDelete,
        onSave,
        onClickCustomKey,
        onKeyboardCustomKey,
        customKeyboardKeys,
        isLoading = false,
      } = propsRef.current;

      if (isLoading) {
        return;
      }

      const customKeyHandler = onKeyboardCustomKey ?? onClickCustomKey;

      if (DIGIT_KEY_PATTERN.test(e.key)) {
        e.preventDefault();
        onClickKey(e.key);
      } else if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        onDelete();
      } else if (e.key === "Enter") {
        if (modality === "keyboard" && isInteractiveTarget(e.target)) {
          return;
        }
        e.preventDefault();
        if (!e.repeat) {
          onSave();
        }
      } else if (customKeyHandler && customKeyboardKeys?.includes(e.key)) {
        e.preventDefault();
        customKeyHandler(e.key);
      }
    };

    root.addEventListener("pointerdown", onPointerDown, true);
    root.addEventListener("keydown", onKeyDown);
    return () => {
      root.removeEventListener("pointerdown", onPointerDown, true);
      root.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return keypadRef;
}

const SHARED_BTN_PROPS: ButtonProps = {
  outline: "none",
  borderRadius: 0,
  minWidth: "unset",
  height: "75px",
  _hover: { color: "white", bg: "blue" },
  _focus: { outline: "none" },
};

function VitalsInputModalKeypad(props: VitalsInputModalKeypadProps) {
  const {
    onClickKey,
    onSave,
    onDelete,
    onClickCustomKey,
    customKeyLabel,
    extraKeys,
    isLoading = false,
  } = props;

  const keypadRef = useKeypadKeyboardInput(props);

  return (
    <Box
      ref={keypadRef}
      display="grid"
      gridTemplateColumns="repeat(3, 100px)"
      gridGap="1px"
      bg="#D3D3D6"
      borderTop="1px solid #D3D3D6"
      borderBottomRadius="10px"
    >
      {NON_ZERO_KEYPAD_INTS.map((num) => (
        <Button
          disabled={isLoading}
          key={num}
          onClick={() => onClickKey(String(num))}
          bg="white"
          color="black"
          {...SHARED_BTN_PROPS}
        >
          {num}
        </Button>
      ))}
      {!onClickCustomKey || !customKeyLabel ? (
        <Box bg="#F7F7F7" />
      ) : (
        <Button
          disabled={isLoading}
          onClick={onClickCustomKey}
          bg="#F7F7F7"
          color="black"
          {...SHARED_BTN_PROPS}
        >
          {customKeyLabel}
        </Button>
      )}
      <Button
        disabled={isLoading}
        onClick={() => onClickKey(String(0))}
        bg="white"
        color="black"
        {...SHARED_BTN_PROPS}
      >
        0
      </Button>
      <Button
        disabled={isLoading}
        onClick={onDelete}
        bg="#F7F7F7"
        {...SHARED_BTN_PROPS}
      >
        <Icon color="red" as={EmrRemove} />
      </Button>
      {extraKeys &&
        extraKeys.map(({ onClick, label }, index) => (
          <Button
            disabled={isLoading}
            key={index}
            onClick={onClick}
            bg="#F7F7F7"
            color="black"
            {...SHARED_BTN_PROPS}
          >
            {label}
          </Button>
        ))}
      <Button
        disabled={isLoading}
        onClick={onSave}
        gridColumn="span 3"
        height="75px"
        bg="#007AFF"
        color="white"
        borderTopRadius="0"
        borderBottomRadius="md"
        minWidth="unset"
      >
        {isLoading ? <Spinner /> : "Enter"}
      </Button>
    </Box>
  );
}

function VitalsInputModal(props: VitalsInputModalProps) {
  const { isOpen, onClose, title, children } = props;
  const contentRef = React.useRef<HTMLElement>(null);

  return (
    <VitalsInputModalOpenContext.Provider value={isOpen}>
      <Modal isOpen={isOpen} onClose={onClose} initialFocusRef={contentRef}>
        <ModalOverlay />
        <ModalContent ref={contentRef} maxWidth="302px">
          <ModalHeader
            padding="24px"
            height="50px"
            textAlign="center"
            fontSize="1.375rem"
            color="#A2A2A2"
          >
            {title}
          </ModalHeader>
          <ModalBody padding="0">{children}</ModalBody>
        </ModalContent>
      </Modal>
    </VitalsInputModalOpenContext.Provider>
  );
}

export type {
  VitalsInputModalProps,
  BaseVitalsInputModalProps,
  VitalsInputModalKeypadExtraKey,
  VitalsInputModalKeypadExtraKeys,
};
export { VitalsInputModal, VitalsInputModalValueBox, VitalsInputModalKeypad };
